export const COMMENT_FILTER_TYPES = {
  OPEN: "open",
  RESOLVED: "resolved",
  IGNORED: "ignored",
} as const;

export type CommentFilterType = (typeof COMMENT_FILTER_TYPES)[keyof typeof COMMENT_FILTER_TYPES];

function truthy(value: unknown): boolean {
  return value === true || value === "true";
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined;
}

const normalizeStatus = (status: unknown): string =>
  typeof status === "string" ? status.trim().toLowerCase() : "";

type CommentLike = Record<string, unknown>;

export function getCommentFilterType(comment: CommentLike | null | undefined): CommentFilterType {
  const status = normalizeStatus(comment?.status);
  if (status === COMMENT_FILTER_TYPES.IGNORED || status === "declined") {
    return COMMENT_FILTER_TYPES.IGNORED;
  }
  if (status === COMMENT_FILTER_TYPES.RESOLVED || status === "accepted") {
    return COMMENT_FILTER_TYPES.RESOLVED;
  }

  if (hasValue(comment?.declinedAt) || hasValue(comment?.declined_at) || truthy(comment?.ignored)) {
    return COMMENT_FILTER_TYPES.IGNORED;
  }

  if (
    hasValue(comment?.acceptedAt) ||
    hasValue(comment?.accepted_at) ||
    hasValue(comment?.resolvedAt) ||
    hasValue(comment?.resolved_at) ||
    truthy(comment?.resolved)
  ) {
    return COMMENT_FILTER_TYPES.RESOLVED;
  }

  return COMMENT_FILTER_TYPES.OPEN;
}

export function filterCommentsByType(comments: unknown, filterType: string | undefined | null): CommentLike[] {
  const selectedType = filterType || COMMENT_FILTER_TYPES.OPEN;
  const list = Array.isArray(comments) ? comments : [];
  return list.filter((comment) => getCommentFilterType(comment as CommentLike) === selectedType) as CommentLike[];
}

export function getCommentCountsByFilter(comments: unknown): Record<CommentFilterType, number> {
  const list = Array.isArray(comments) ? comments : [];
  const counts = {
    [COMMENT_FILTER_TYPES.OPEN]: 0,
    [COMMENT_FILTER_TYPES.RESOLVED]: 0,
    [COMMENT_FILTER_TYPES.IGNORED]: 0,
  };

  list.forEach((comment) => {
    const type = getCommentFilterType(comment as CommentLike);
    counts[type] += 1;
  });

  return counts;
}

export function getCommentFilterLabel(filterType: string): string {
  switch (filterType) {
    case COMMENT_FILTER_TYPES.RESOLVED:
      return "Resolved";
    case COMMENT_FILTER_TYPES.IGNORED:
      return "Ignored";
    case COMMENT_FILTER_TYPES.OPEN:
    default:
      return "Open";
  }
}
